import { useEffect, useRef, useState } from "react";
import { register, call, remove } from "../funcStore";
import Header from "../components/layout/Header";
import MainTabs from "../components/layout/MainTabs";
import Footer from "../components/layout/Footer";
import VTuberProfile from "../components/vtuber/VTuberProfile";
import StreamTrends from "../components/trends/StreamTrends";
import VTuberVideoList from "../components/vtuber/VTuberVideoList";
import { sortAttribute } from "../utils/attributes";
import { parseYoutubeChannelId } from "../utils/youtubeUrl";
import { logAction } from "../api/logApi";
import {
  createVtuberDraft,
} from "../api/createApi";
import {
  AFFILIATION_OPTIONS,
  LANGUAGE_OPTIONS,
  HAIR_COLOR_OPTIONS,
  EYE_COLOR_OPTIONS,
  HAIR_LENGTH_OPTIONS,
  ACCESSORY_OPTIONS,
} from "../data/mockVtubers";

const COMPONENT_KEY = "createVtuberPage";

// クリップボードへコピーする。
// navigator.clipboard は https / localhost でしか使えないため、
// 使えない環境では execCommand にフォールバックする
const copyToClipboard = (text) => {
  if (navigator.clipboard?.writeText) {
    // ユーザー操作直後に呼ぶ必要があるので await せずに投げる
    navigator.clipboard.writeText(text).catch(() => {});
    return true;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();

  let copied = false;

  try {
    copied = document.execCommand("copy");
  } catch (err) {
    copied = false;
  }

  document.body.removeChild(textarea);

  return copied;
};

// 運営アカウントのX User ID。DM作成画面への遷移に使う
// TODO: vfinder運営アカウントの実際のX User IDに差し替える
const X_RECIPIENT_ID = "2093926035599593472";

// 属性の入力欄。multiple=false のものはラジオ(1つだけ選択)になる
const ATTRIBUTE_SECTIONS = [
  {
    key: "basic",
    title: "基本情報",
    open: true,
    groups: [
      { key: "affiliation", title: "個人勢 / 企業勢", options: AFFILIATION_OPTIONS, multiple: false },
      { key: "language", title: "メイン言語", options: LANGUAGE_OPTIONS, multiple: false },
    ],
  },
  {
    key: "appearance",
    title: "見た目",
    open: true,
    groups: [
      { key: "hairColor", title: "髪色", options: HAIR_COLOR_OPTIONS, multiple: false },
      { key: "eyeColor", title: "瞳の色", options: EYE_COLOR_OPTIONS, multiple: false },
      { key: "hairLength", title: "髪の長さ", options: HAIR_LENGTH_OPTIONS, multiple: false },
      { key: "accessory", title: "アクセサリー", options: ACCESSORY_OPTIONS, multiple: true },
    ],
  },
];

const FUNC_KEYS = [
  `${COMPONENT_KEY}.getState`,
  `${COMPONENT_KEY}.getProfile`,
  `${COMPONENT_KEY}.getAttributes`,
  `${COMPONENT_KEY}.getVtuber`,
  `${COMPONENT_KEY}.getVideos`,
  `${COMPONENT_KEY}.getPageUrl`,
  `${COMPONENT_KEY}.getErrorMessage`,
  `${COMPONENT_KEY}.getDmText`,
  `${COMPONENT_KEY}.copyDmText`,
  `${COMPONENT_KEY}.save`,
  `${COMPONENT_KEY}.edit`,
  `${COMPONENT_KEY}.report`,
];

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
  },
  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "24px",
    maxWidth: "640px",
    margin: "0 auto",
    width: "80%",
  },
  title: {
    margin: 0,
  },
  description: {
    margin: 0,
    fontSize: "0.9rem",
    color: "#555",
    lineHeight: 1.7,
  },
  notice: {
    margin: 0,
    padding: "12px",
    background: "#f4f2fb",
    border: "1px solid #ded8f3",
    borderRadius: "6px",
    fontSize: "0.85rem",
    color: "#4b3f8f",
    lineHeight: 1.7,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "0.85rem",
    fontWeight: "bold",
    color: "#444",
  },
  required: {
    color: "#a33",
    fontSize: "0.75rem",
    marginLeft: "4px",
  },
  input: {
    padding: "10px",
    fontSize: "0.95rem",
    fontFamily: "inherit",
    border: "1px solid #ccc",
    borderRadius: "4px",
  },
  textarea: {
    minHeight: "120px",
    padding: "10px",
    fontSize: "0.95rem",
    fontFamily: "inherit",
    border: "1px solid #ccc",
    borderRadius: "4px",
    resize: "vertical",
  },
  urlRow: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },
  urlInput: {
    flex: 1,
    minWidth: "200px",
    padding: "10px",
    fontSize: "0.95rem",
    fontFamily: "inherit",
    border: "1px solid #ccc",
    borderRadius: "4px",
  },
  iconRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  icon: {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    objectFit: "cover",
  },
  iconNote: {
    margin: 0,
    fontSize: "0.8rem",
    color: "#888",
  },
  section: {
    border: "1px solid #ddd",
    borderRadius: "6px",
    padding: "12px",
    background: "#fff",
  },
  sectionTitle: {
    fontSize: "0.95rem",
    fontWeight: "bold",
    color: "#333",
    cursor: "pointer",
  },
  groupList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginTop: "12px",
  },
  groupTitle: {
    margin: "0 0 6px",
    fontSize: "0.85rem",
    fontWeight: "bold",
    color: "#444",
  },
  optionList: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  optionLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "0.85rem",
    background: "#f5f5f7",
    border: "1px solid #ddd",
    borderRadius: "16px",
    padding: "6px 12px",
    cursor: "pointer",
  },
  submitButton: {
    alignSelf: "flex-start",
    border: "none",
    background: "#4b3f8f",
    color: "#fff",
    borderRadius: "6px",
    padding: "12px 28px",
    fontSize: "0.95rem",
    cursor: "pointer",
  },
  reportButton: {
    alignSelf: "flex-start",
    border: "none",
    background: "#111",
    color: "#fff",
    borderRadius: "6px",
    padding: "12px 28px",
    fontSize: "0.95rem",
    cursor: "pointer",
  },
  editButton: {
    alignSelf: "flex-start",
    border: "1px solid #999",
    background: "#fff",
    color: "#444",
    borderRadius: "6px",
    padding: "8px 20px",
    fontSize: "0.9rem",
    cursor: "pointer",
  },
  disabledButton: {
    opacity: 0.5,
    cursor: "default",
  },
  detailBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  previewCard: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "16px",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: "8px",
  },
  previewHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  previewName: {
    margin: 0,
    fontSize: "1.05rem",
  },
  previewDescription: {
    margin: 0,
    fontSize: "0.85rem",
    color: "#555",
    whiteSpace: "pre-wrap",
  },
  tagList: {
    listStyle: "none",
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    padding: 0,
    margin: 0,
  },
  tagItem: {
    border: "1px solid #ccc",
    borderRadius: "12px",
    padding: "2px 10px",
    fontSize: "0.8rem",
    color: "#555",
  },
  pageUrl: {
    margin: 0,
    fontSize: "0.85rem",
    color: "#4b3f8f",
    wordBreak: "break-all",
  },
  dmTextBox: {
    margin: 0,
    padding: "12px",
    background: "#f7f7f9",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "0.85rem",
    fontFamily: "inherit",
    color: "#333",
    whiteSpace: "pre-wrap",
    lineHeight: 1.7,
  },
  copyButton: {
    alignSelf: "flex-start",
    border: "1px solid #999",
    background: "#fff",
    color: "#444",
    borderRadius: "6px",
    padding: "8px 20px",
    fontSize: "0.9rem",
    cursor: "pointer",
  },
  successText: {
    margin: 0,
    color: "#2a7a2a",
    fontSize: "0.9rem",
    lineHeight: 1.7,
  },
  errorText: {
    margin: 0,
    color: "#a33",
    fontSize: "0.9rem",
  },
};

function CreateVtuberPage() {
  // state
  const STATE_BEFORE_INIT = "beforeInit";
  const STATE_EDITING = "editing";
  const STATE_INVALID = "invalid";
  const STATE_SAVING = "saving";
  const STATE_SAVED = "saved";
  const STATE_REPORTED = "reported";
  const STATE_ERROR = "error";

  const [state, setState] = useState(STATE_BEFORE_INIT);

  const stateRef = useRef(state);
  stateRef.current = state;

  // data
  const dataRef = useRef({
    draftId: null,
    pageUrl: "",
    // 作成APIのレスポンス(詳細画面と同じ形)。保存後はこれを詳細画面としてそのまま描画する
    vtuber: null,
    saveCount: 0,
    dmTextCopied: false,
    profile: {
      channelId: "",
      name: "",
      description: "",
      iconUrl: "",
      youtubeUrl: "",
      subscriberCount: null,
    },
    attributes: [],
    errorMessage: "",
  });

  const formRef = useRef(null);

  const buildPageUrl = (draftId) =>
    `${window.location.origin}/?vtuberId=${draftId ?? ""}`;

  // 本人から運営へ送る掲載報告DMの本文
  const buildDmText = () =>
    [
      "vfinder掲載報告",
      `Vtuber名：${dataRef.current.vtuber?.name ?? ""}`,
      "掲載よろしくお願いいたします！"
    ].join("\n");

  // x.com のWeb版では text= が本文に反映されないことがあるため、
  // 本文はクリップボードにも入れて貼り付けできるようにしている
  const buildDmUrl = () =>
    `https://x.com/messages/compose?recipient_id=${X_RECIPIENT_ID}&text=${encodeURIComponent(buildDmText())}`;

  const init = () => {
    register(`${COMPONENT_KEY}.getState`, () => stateRef.current);

    register(`${COMPONENT_KEY}.getProfile`, () => dataRef.current.profile);

    register(`${COMPONENT_KEY}.getAttributes`, () => dataRef.current.attributes);

    register(`${COMPONENT_KEY}.getVtuber`, () => dataRef.current.vtuber);

    register(
      `${COMPONENT_KEY}.getVideos`,
      () => dataRef.current.vtuber?.videos ?? [],
    );

    register(`${COMPONENT_KEY}.getPageUrl`, () => dataRef.current.pageUrl);

    register(
      `${COMPONENT_KEY}.getErrorMessage`,
      () => dataRef.current.errorMessage,
    );

    register(`${COMPONENT_KEY}.getDmText`, () => buildDmText());

    register(`${COMPONENT_KEY}.copyDmText`, () => {
      if (stateRef.current !== STATE_REPORTED) {
        return;
      }

      // 本文は常に画面に出しているので、コピー可否でUIは変えない(State遷移も不要)
      dataRef.current.dmTextCopied = copyToClipboard(buildDmText());
    });

    register(`${COMPONENT_KEY}.save`, async (values) => {
      if (
        stateRef.current === STATE_SAVING
      ) {
        return;
      }

      dataRef.current.attributes = values.attributes;

      if (!values.youtubeUrl) {
        dataRef.current.errorMessage =
          "YouTubeチャンネルURLは必須です。";
        setState(STATE_INVALID);
        return;
      }

      // 日本語ハンドルはコピー時にパーセントエンコードされているのでデコードする
      const channelId = parseYoutubeChannelId(values.youtubeUrl);

      if (!channelId) {
        dataRef.current.errorMessage =
          "YouTubeチャンネルURLを認識できませんでした。https://www.youtube.com/@... の形式で入力してください。";
        setState(STATE_INVALID);
        return;
      }

      dataRef.current.profile = {
        ...dataRef.current.profile,
        channelId,
        youtubeUrl: values.youtubeUrl,
      };

      dataRef.current.errorMessage = "";
      setState(STATE_SAVING);

      const draft = {
        channelId: dataRef.current.profile.channelId,
        attributes: dataRef.current.attributes,
      };

      try {
        const saved = await createVtuberDraft(draft);

        dataRef.current.vtuber = sortAttribute(saved);
        dataRef.current.draftId = saved.id;
        dataRef.current.pageUrl = buildPageUrl(saved.id);
        dataRef.current.saveCount += 1;

        logAction("vtuber_draft_save", {
          draftId: dataRef.current.draftId,
          attributeCount: dataRef.current.attributes.length,
        });
        setState(STATE_SAVED);
      } catch (err) {
        dataRef.current.errorMessage = err.message;
        logAction("vtuber_draft_save_error", { error: err.message });
        setState(STATE_ERROR);
      }
    });

    register(`${COMPONENT_KEY}.edit`, () => {
      if (
        stateRef.current !== STATE_SAVED &&
        stateRef.current !== STATE_REPORTED
      ) {
        return;
      }

      dataRef.current.errorMessage = "";
      setState(STATE_EDITING);
    });

    // 掲載報告。掲載許諾の確認は本人からのX DMで行うため、ここではDM作成画面を開く
    register(`${COMPONENT_KEY}.report`, async () => {
      if (stateRef.current !== STATE_SAVED) {
        return;
      }

      // クリック直後に実行しないとコピー・ポップアップともにブロックされるため、通信より先に行う
      dataRef.current.dmTextCopied = copyToClipboard(buildDmText());
      window.open(buildDmUrl(), "_blank", "noopener");

      logAction("vtuber_draft_report", { draftId: dataRef.current.draftId });
      setState(STATE_REPORTED);
    });

    setState(STATE_EDITING);
  };

  // effect
  useEffect(() => {
    switch (state) {
      case STATE_BEFORE_INIT:
        init();
        break;

      case STATE_EDITING:
      case STATE_INVALID:
      case STATE_SAVING:
      case STATE_SAVED:
      case STATE_REPORTED:
      case STATE_ERROR:
        break;

      default:
        console.error(`Unknown state: ${state}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    return () => {
      FUNC_KEYS.forEach((key) => remove(key));
    };
  }, []);

  if (state === STATE_BEFORE_INIT) {
    return null;
  }

  const profile = dataRef.current.profile;
  const attributes = dataRef.current.attributes;
  const vtuber = dataRef.current.vtuber;
  const errorMessage = dataRef.current.errorMessage;
  const isFormVisible = state !== STATE_SAVED && state !== STATE_REPORTED;
  const isBusy = state === STATE_SAVING;

  const collectFormValues = () => {
    const form = formRef.current;

    if (!form) {
      return { youtubeUrl: "", name: "", description: "", attributes: [] };
    }

    const checked = Array.from(
      form.querySelectorAll(
        'input[type="checkbox"]:checked, input[type="radio"]:checked',
      ),
    ).map((input) => input.value);

    const getValue = (name) => {
      const element = form.elements.namedItem(name);
      return element ? element.value.trim() : "";
    };

    return {
      youtubeUrl: getValue("youtubeUrl"),
      name: getValue("vtuberName"),
      description: getValue("description"),
      attributes: checked,
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    call(`${COMPONENT_KEY}.save`, collectFormValues());
  };

  // 入力欄でEnterを押しただけで保存されないようにする。
  // 改行が必要なtextareaと、明示的に押されたボタンは通す。
  const handleKeyDown = (e) => {
    if (e.key !== "Enter") {
      return;
    }

    const tagName = e.target.tagName;

    if (tagName === "TEXTAREA" || tagName === "BUTTON") {
      return;
    }

    e.preventDefault();
  };

  const handleReportClick = () => {
    call(`${COMPONENT_KEY}.report`);
  };

  const handleEditClick = () => {
    call(`${COMPONENT_KEY}.edit`);
  };

  const handleCopyDmTextClick = () => {
    call(`${COMPONENT_KEY}.copyDmText`);
  };

  return (
    <div style={styles.page}>
      <Header />

      <MainTabs />

      <main
        style={
          isFormVisible ? styles.main : { ...styles.main, maxWidth: "720px" }
        }
      >
        <h2 style={styles.title}>あなたのVTuberページを作る</h2>

        <p style={styles.description}>
          配信ジャンル・配信頻度・配信時間帯からVTuberを探せる検索サイト「vfinder」に、
          あなた自身のページを無料で作成できます。
        </p>

        {isFormVisible && (
          <form
            ref={formRef}
            style={styles.form}
            onSubmit={handleSubmit}
            onKeyDown={handleKeyDown}
          >
            <div style={styles.field}>
              <span style={styles.label}>
                YouTubeチャンネルURL
                <span style={styles.required}>必須</span>
              </span>
              <div style={styles.urlRow}>
                <input
                  name="youtubeUrl"
                  type="url"
                  style={styles.urlInput}
                  defaultValue={profile.youtubeUrl}
                  placeholder="https://www.youtube.com/@your-channel"
                />
              </div>
            </div>

            {ATTRIBUTE_SECTIONS.map((section) => (
              <details
                key={section.key}
                style={styles.section}
                open={section.open}
              >
                <summary style={styles.sectionTitle}>{section.title}</summary>

                <div style={styles.groupList}>
                  {section.groups.map((group) => (
                    <div key={group.key}>
                      <p style={styles.groupTitle}>{group.title}</p>
                      <div style={styles.optionList}>
                        {group.options.map((option) => (
                          <label key={option.value} style={styles.optionLabel}>
                            <input
                              type={group.multiple ? "checkbox" : "radio"}
                              name={group.key}
                              value={option.value}
                              defaultChecked={attributes.includes(option.value)}
                            />
                            {option.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            ))}

            <button
              type="submit"
              style={{
                ...styles.submitButton,
                ...(isBusy ? styles.disabledButton : {}),
              }}
              disabled={isBusy}
            >
              {state === STATE_SAVING ? "保存中..." : "この内容でページを作る"}
            </button>

            {(state === STATE_INVALID || state === STATE_ERROR) && (
              <p style={styles.errorText}>{errorMessage}</p>
            )}
          </form>
        )}

        {!isFormVisible && vtuber && (
          <>
            {/* 保存後は通常の詳細画面と同じ構成で表示する */}
            <div
              key={`${vtuber.id}-${dataRef.current.saveCount}`}
              style={styles.detailBlock}
            >
              <VTuberProfile vtuber={vtuber} />

              <StreamTrends vtuber={vtuber} />

              <VTuberVideoList
                vtuberId={vtuber.id}
                parentKey={COMPONENT_KEY}
              />
            </div>

            <p style={styles.pageUrl}>{dataRef.current.pageUrl}</p>

            {state === STATE_SAVED && (
              <>
                <p style={styles.description}>
                  下書きを保存しました。内容をご確認のうえ、掲載を希望する場合は運営へ掲載報告を送信してください。
                  ボタンを押すと、XのDM作成画面が本文入力済みの状態で開きます。
                </p>

                <button
                  type="button"
                  style={styles.reportButton}
                  onClick={handleReportClick}
                >
                  運営に掲載を報告する
                </button>
              </>
            )}

            {state === STATE_REPORTED && (
              <>
                <p style={styles.successText}>
                  掲載報告ありがとうございます。
                  <br />
                  XのDM送信が完了すると、運営が内容を確認したうえでページを公開します。
                  <br />
                  DMがまだ送信できていない場合は、開いたDM画面から送信してください。
                </p>

                <p style={styles.description}>
                  {dataRef.current.dmTextCopied
                    ? "下記の本文はコピー済みです。DM画面に本文が入っていない場合は貼り付けて送信してください。"
                    : "DM画面に本文が入っていない場合は、下記をコピーして貼り付けて送信してください。"}
                </p>

                <p style={styles.dmTextBox}>{call(`${COMPONENT_KEY}.getDmText`)}</p>

                <button
                  type="button"
                  style={styles.copyButton}
                  onClick={handleCopyDmTextClick}
                >
                  本文をコピーする
                </button>
              </>
            )}

            <button
              type="button"
              style={styles.editButton}
              onClick={handleEditClick}
            >
              内容を修正する
            </button>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}

export default CreateVtuberPage;
